import store from "../store";
import { initializeStake } from "../store/slices/selectors";
import { resize } from "../store/slices/layout";
import { updateAutoplay, setAutoplay } from "../store/slices/autoplay";
import {
  toggleButtons,
  toggleButtonsAvailability,
  toggleButtonsVisibility,
  setButtonType,
  setButtonLock,
} from "../store/slices/buttons";
import {
  dispatchEvent,
  getConfig,
  history,
  getCurrency,
  loadTexts,
  waitFrame,
} from "../utils";
import * as Utils from "../utils";
import { ErrorType, getErrorMessage } from "../components/modals/Error";
import { setTheme } from "../store/slices/theme";
import { ModalType } from "../store/slices/modals";
import {
  hideSpinnerOverlay,
  showSpinnerOverlay,
} from "../store/slices/spinner-overlay";
import { showSoundOverlay } from "../store/slices/sound-overlay";
import { setRealityCheck } from "../store/slices/reality-check";
import {
  freeze,
  unfreeze,
  showBars,
  hideBars,
  initBalance,
  decreaseBalance,
  toggleMute,
  toggleFullscreen,
  toggleLogo,
  toggleTurboSpin,
  setGameName,
  setSoundLevel,
  setLobby,
  setBank,
  setCurrency,
  setBalance,
  setAccountUri,
  setLanguage,
  setRTP,
  setMaxWin,
  setDynamicPaytable,
  setSlamStop,
  setVersion,
} from "../store/slices/game-details";
import {
  showLoadingScreen,
  hideLoadingScreen,
  setProggress,
} from "../store/slices/loading-screen";
import { setError } from "../store/slices/error";
import { openModal } from "../store/slices/modals";
import { addHistoryRound, addHistoryRounds } from "../store/slices/history";
import { setMessage } from "../store/slices/messages";
import { updateFreeRounds } from "../store/slices/free-rounds";
import {
  InitialCfg,
  EVENTS,
  Button,
  InitialData,
  FreeRoundsData,
  MaxWin,
} from "./Types";
import PrivateAPI from "./PrivateAPI";
import { setPreviousWin } from "../store/slices/previous-win";

const DEFAULT_BTNS: Button[] = [
  "spin",
  "stake",
  "autoplay",
  "turbo",
  "info",
  "buyBonus",
];

const PublicAPI = {
  get version() {
    return process.env.REACT_APP_VERSION;
  },
  show() {
    store.dispatch(showBars());
    PrivateAPI.showFreeRounds();
  },

  hide() {
    store.dispatch(hideBars());
  },

  setGameName(name: string) {
    store.dispatch(setGameName(name));
  },

  showLoadingScreen() {
    store.dispatch(showLoadingScreen());
  },

  hideLoadingScreen() {
    store.dispatch(hideLoadingScreen());
  },

  updateLoadingScreen(progress: number) {
    store.dispatch(setProggress(progress));
  },

  toggleButtons(buttons: { [button: string]: boolean }) {
    store.dispatch(toggleButtons(buttons));
  },

  disableInteraction(buttons?: Button[]) {
    const btns = buttons ?? [
      "spin",
      "autoplay",
      "stake",
      "info",
      "turbo",
      "home",
      "account",
      "buyBonus",
    ];
    const cfg = btns.reduce((obj, btn) => ({ ...obj, [btn]: false }), {});

    PublicAPI.toggleButtons(cfg);
  },

  enableInteraction(buttons: Button[]) {
    const btns = buttons ?? [
      "spin",
      "autoplay",
      "stake",
      "info",
      "sound",
      "home",
      "bank",
      "fullscreen",
      "turbo",
      "account",
      "buyBonus",
    ];

    const cfg = btns.reduce((obj, btn) => ({ ...obj, [btn]: true }), {});
    PublicAPI.toggleButtons(cfg);
  },

  hideButtons(buttons: Button[] = DEFAULT_BTNS) {
    const cfg = buttons.reduce((obj, btn) => ({ ...obj, [btn]: false }), {});
    store.dispatch(toggleButtonsVisibility(cfg));
  },

  showButtons(buttons: Button[] = DEFAULT_BTNS) {
    const cfg = buttons.reduce((obj, btn) => ({ ...obj, [btn]: true }), {});
    store.dispatch(toggleButtonsVisibility(cfg));
  },

  resize(width: number, height: number, pixelRatio: number) {
    store.dispatch(resize({ width, height, pixelRatio }));
  },

  toggleMute(mute: boolean) {
    const event = mute ? EVENTS.MUTE : EVENTS.UNMUTE;
    const level = store.getState().details.sound.level;

    // if the sound is unmuted from sound with volume level of 0, the level is set to 1
    if (!mute && level === 0) {
      PublicAPI.setSoundLevel(1);
    }
    dispatchEvent(event);
    store.dispatch(toggleMute(mute));
  },

  setSoundLevel(level: number) {
    const mute = store.getState().details.sound.mute;
    store.dispatch(setSoundLevel(level));
    dispatchEvent(EVENTS.SOUND_LEVEL, level);

    // if the sound is muted but the sound level is greater that 0, the sound is automatically unmuted
    if (level > 0 && mute) {
      PublicAPI.toggleMute(false);
    }
  },

  toggleFullscreen(state: boolean) {
    if (!document.fullscreenEnabled) return;

    if (state) {
      document.body.requestFullscreen();
    } else {
      document.exitFullscreen();
    }

    if (!document.onfullscreenchange) {
      document.onfullscreenchange = () =>
        store.dispatch(toggleFullscreen(!!document.fullscreenElement));
    }
  },

  playSound(sound: string) {
    dispatchEvent(EVENTS.PLAY_SOUND, sound);
  },

  // setLobby(lobby: string | null) {
  //   store.dispatch(setLobby(lobby || ""));
  // },

  getEvents() {
    return EVENTS;
  },

  // if full is true the whole stake stake is returned,
  // otherwise just the "amount" which is the bet per line
  getStake(full = false) {
    const { freeRounds, selectors } = store.getState();
    if (freeRounds.active) return freeRounds.stake;

    return full ? selectors.stake : selectors.stake.value;
  },

  stopAutoplay(disableButtons?: boolean) {
    if (!store.getState().autoplay.active) return;

    store.dispatch(setAutoplay({ value: 0 }));
    if (disableButtons) {
      PublicAPI.disableInteraction();
    }

    store.dispatch(setButtonType({ button: "spin", type: "spin" }));
    dispatchEvent(EVENTS.AUTOPLAY_STOPPED);
  },

  getAutoplay() {
    return store.getState().autoplay;
  },

  getFreeRounds() {
    const { active, amount, stake } = store.getState().freeRounds;

    return { active, amount, stake };
  },

  updateAutoplay(balance: any | null) {
    if (!store.getState().autoplay.active) return;

    store.dispatch(updateAutoplay(balance));
    const isActive = store.getState().autoplay.value > 0;
    const type = isActive ? "stop" : "spin";
    store.dispatch(setButtonType({ button: "spin", type }));

    if (!isActive) dispatchEvent(EVENTS.AUTOPLAY_STOPPED);
  },

  // called before the first response from the server;
  async initialize({
    game,
    language,
    lobby,
    bank,
    accountUri,
    hasFullscreen,
    hasLogo,
  }: InitialCfg) {
    store.dispatch(setGameName(game.name));
    store.dispatch(setLanguage(language));
    store.dispatch(setLobby(lobby || ""));
    store.dispatch(setBank(bank || ""));
    store.dispatch(setAccountUri(accountUri || ""));
    store.dispatch(toggleLogo(typeof hasLogo !== "boolean" || hasLogo));
    store.dispatch(toggleButtonsAvailability({ fullscreen: hasFullscreen }));
  },

  // called right after the first response from the server;
  async setInitialData({
    stakeValues,
    defaultBet,
    balance,
    currency,
    rtp,
    maxWin,
    slamStop,
    version,
    buyFeature,
    autoplay,
    hasTurbo,
    hasTimer,
    hasHistory,
  }: InitialData) {
    const cfg = await getConfig();
    store.dispatch(initializeStake({ stakeValues, defaultBet }));
    store.dispatch(initBalance(balance));
    store.dispatch(setRTP(rtp ?? {}));
    store.dispatch(setMaxWin(maxWin));
    store.dispatch(
      setDynamicPaytable(
        typeof maxWin === "number" ? maxWin === MaxWin.Germany : !!maxWin
      )
    );
    store.dispatch(
      setSlamStop((slamStop ?? hasTurbo ?? false) && cfg.turboSpin)
    );
    store.dispatch(setVersion(version ?? "N/A"));
    store.dispatch(setCurrency(getCurrency(currency)));

    PrivateAPI.toggleHistory(typeof hasHistory !== "boolean" || hasHistory);
    if (hasTimer) PrivateAPI.startTimer();
    store.dispatch(
      toggleButtonsAvailability({
        autoplay: autoplay.available,
        turbo: !!hasTurbo && cfg.turboSpin,
      })
    );
    PrivateAPI.initBuyBonus(buyFeature, rtp);
  },

  setMessage(title = "", message = "", timer = -1) {
    if (!title && !message) return;

    store.dispatch(openModal("message"));
    store.dispatch(setMessage({ title, message, timer }));
  },

  showPreviosWin(amount: number) {
    if (!amount || amount <= 0) return;

    store.dispatch(openModal("previousWin"));
    store.dispatch(setPreviousWin(amount));
  },

  setLanguage(language: string) {
    store.dispatch(setLanguage(language));
  },

  getBalance() {
    return store.getState().details.balance;
  },

  // the win parameter is only used to update the "net balance"
  setBalance(value: string, win?: string) {
    const fr = (store.getState() as any).freeRounds;
    store.dispatch(
      setBalance({ value, win: win ?? "0.00", fullUpdate: !fr.active })
    );
  },

  decreaseBalance(value: string, next: string) {
    const fr = (store.getState() as any).freeRounds;
    store.dispatch(decreaseBalance({ value, next, fullUpdate: !fr.active }));

    return store.getState().details.balance;
  },
  freeze() {
    store.dispatch(freeze());
  },
  unfreeze() {
    store.dispatch(unfreeze());
  },

  isFrozen() {
    return store.getState().details.frozen;
  },

  getTurboState() {
    const state = store.getState();
    return {
      available: state.buttons.turbo.available,
      active: state.details.turbo,
    };
  },

  startSpin(delay = 0) {
    const state = store.getState();
    const type =
      (state.details.slamStop && !state.details.turbo) || state.autoplay.active
        ? "stop"
        : "spin";
    const start = () => {
      dispatchEvent(EVENTS.SPIN, type);
      store.dispatch(setButtonType({ button: "spin", type }));
    };
    if (delay > 0) {
      setTimeout(start, delay);
    } else {
      start();
    }
  },

  stopSpin(delay = 0) {
    const stop = () => {
      dispatchEvent(EVENTS.STOP_SPIN);
      store.dispatch(setButtonType({ button: "spin", type: "spin" }));
    };

    if (delay > 0) {
      setTimeout(stop, delay);
    } else {
      stop();
    }
  },

  setSpinButton(type: "spin" | "stop") {
    store.dispatch(setButtonType({ button: "spin", type }));
  },

  setError(error: ErrorType) {
    store.dispatch(setError(error));
    store.dispatch(openModal("error"));
  },

  addHistoryRounds(data: any) {
    const rounds = history.parseRounds(data.rounds);
    store.dispatch(addHistoryRounds({ rounds, error: data.error }));
  },

  async addHistoryRound(data: any) {
    if (data.error) return store.dispatch(addHistoryRound(data));

    const cfg = await getConfig();
    const customParse = cfg.history?.parse ?? ((round: any) => round);

    store.dispatch(
      addHistoryRound(customParse(history.parseRound(data, cfg), data, Utils))
    );
  },

  toggleModal(modal: ModalType, force?: true) {
    const isOpened = store.getState().modals.active === modal;
    const isForceBool = typeof force === "boolean";

    if (isForceBool && isOpened === force) return;

    if (!isOpened || (isForceBool && force)) {
      PrivateAPI.openModal(modal);
    } else {
      PrivateAPI.closeModal(modal);
    }
  },

  updateFreeRounds({ amount, stake, totalWin }: FreeRoundsData) {
    if (typeof amount !== "number" || isNaN(amount)) return;
    // defaults to 0 if it is missing or it can be parsed to float
    // const stakeParsed = stake || "";
    store.dispatch(updateFreeRounds({ amount, totalWin, stake }));

    const state = (store.getState() as any).freeRounds;
    if (amount !== 0 && state.active) {
      console.warn("stake of the free rounds was changed!");
      PrivateAPI.setStake(stake);
    }
    if (amount === 0 && state.active) {
      const autoplay =
        state.cache && typeof state.cache.autoplay === "boolean"
          ? state.cache.autoplay
          : true;

      const buyBonus =
        state.cache && typeof state.cache.buyBonus === "boolean"
          ? state.cache.buyBonus
          : false;

      store.dispatch(
        toggleButtonsAvailability({ stake: true, autoplay, buyBonus })
      );

      PrivateAPI.setStake(state.cache.stake.value);
      if (state.active) store.dispatch(openModal("freeRoundsEnd"));
    } else {
      // if already shown it will be handled in the "showFreeRounds"
      setTimeout(() => {
        PrivateAPI.showFreeRounds();
      }, 0);
    }
  },

  isLoaded() {
    return store.getState().details.loaded;
  },

  toggleTurboSpin(value: boolean) {
    store.dispatch(toggleTurboSpin(value));
    dispatchEvent(EVENTS.TOGGLE_TURBO_SPIN, value);
  },

  setTheme(theme: any) {
    store.dispatch(setTheme(theme));
  },

  showSoundOverlay() {
    store.dispatch(showSoundOverlay());
  },

  showSpinnerOverlay(config?: { style?: any; size?: number; stroke?: number }) {
    store.dispatch(showSpinnerOverlay(config ?? {}));
  },

  hideSpinnerOverlay() {
    store.dispatch(hideSpinnerOverlay());
  },

  showRealityCheck(time: number, historyURL: string) {
    store.dispatch(setRealityCheck({ time, historyURL }));
  },

  getCurrencyFormat(cfg: any) {
    return getCurrency(cfg);
  },

  async lockSpinButton(time: number) {
    const timestamp = Date.now();
    // 0.01 is used instead of zero so the button will be locked at the first frame not the second one
    store.dispatch(
      setButtonLock({ button: "spin", progress: 0.01, timestamp })
    );

    const lockTicker = async (
      progress: number,
      time: number,
      before?: number
    ): Promise<any> => {
      const state = store.getState().buttons.spin.locked;
      if (state.timestamp !== timestamp) return Promise.resolve();

      store.dispatch(setButtonLock({ button: "spin", progress }));
      const now = await waitFrame();
      const delta = before ? now - before : 0;
      const step = delta / (time * 1000);

      if (progress < 1) {
        return lockTicker(progress + step, time, now);
      } else {
        return Promise.resolve();
      }
    };

    return lockTicker(0.01, time);
  },

  async getErrorMessage(error: ErrorType) {
    const code = error?.code ?? error?.ErrorCode ?? -1;
    const { language } = store.getState().details;
    const common = await loadTexts("common", language ?? "en");

    return getErrorMessage({ ...error, code }, common);
  },

  openPaytable() {
    PrivateAPI.openModal("pays");
  },
};

export default PublicAPI;
